import { createFileRoute, Outlet, useMatch, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useDeleteConversation } from "@/api/mutations/messenger";
import { usePublicConfig } from "@/api/queries/config";
import { useConversations } from "@/api/queries/messenger";
import { ConversationList } from "@/components/messenger";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { useMediaQuery } from "@/hooks/use-media-query";
import { usePageMeta } from "@/hooks/use-page-meta";
import { useVisualViewport } from "@/hooks/use-visual-viewport";
import { useToastStore } from "@/stores/toast-store";

export const Route = createFileRoute("/_app/messenger")({
	component: MessengerLayout,
});

function MessengerLayout() {
	const navigate = useNavigate();
	const isDesktop = useMediaQuery("(min-width: 1024px)");
	const { height: vpHeight, keyboardOpen } = useVisualViewport();
	const { data: config } = usePublicConfig();

	// Prüfen ob ein Chat aktiv ist (Child-Route /$id)
	const chatMatch = useMatch({ from: "/_app/messenger/$id", shouldThrow: false });
	const activeConversationId = chatMatch?.params?.id ?? null;

	usePageMeta({
		title: config ? `Messenger - ${config.seo.defaultTitle}` : undefined,
	});

	const {
		conversations,
		isLoading,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
		error: conversationsError,
	} = useConversations();
	const addToast = useToastStore((s) => s.addToast);
	const deleteConversation = useDeleteConversation();
	const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
	const lastErrorToastAt = useRef(0);
	const ERROR_TOAST_COOLDOWN_MS = 3000;

	useEffect(() => {
		if (!conversationsError) return;
		if (Date.now() - lastErrorToastAt.current < ERROR_TOAST_COOLDOWN_MS) return;
		lastErrorToastAt.current = Date.now();
		addToast({
			type: "warning",
			message: "Konversationen konnten nicht geladen werden.",
		});
	}, [conversationsError, addToast]);

	const handleSelectConversation = (id: string) => {
		navigate({ to: "/messenger/$id", params: { id } });
	};

	const handleDeleteConversation = (id: string) => {
		setDeleteTarget(id);
	};

	const confirmDelete = () => {
		if (!deleteTarget) return;
		deleteConversation.mutate(
			{ conversationId: deleteTarget },
			{
				onSuccess: () => {
					setDeleteTarget(null);
					// Wenn die gelöschte Konversation aktiv war, zurück zur Liste
					if (activeConversationId === deleteTarget) {
						navigate({ to: "/messenger" });
					}
				},
			},
		);
	};

	// Desktop: Zwei-Spalten-Layout
	if (isDesktop) {
		return (
			<>
				<div className="-mx-4 -mt-6 -mb-6 flex h-[calc(100dvh-4rem)]">
					{/* Konversationsliste */}
					<div className="w-80 border-r border-border shrink-0 overflow-hidden">
						<ConversationList
							conversations={conversations}
							activeConversationId={activeConversationId}
							onSelectConversation={handleSelectConversation}
							onDeleteConversation={handleDeleteConversation}
							isLoading={isLoading}
							hasNextPage={hasNextPage}
							isFetchingNextPage={isFetchingNextPage}
							onFetchNextPage={fetchNextPage}
						/>
					</div>

					{/* Chat oder EmptyState (via Outlet) */}
					<div className="flex-1 overflow-hidden">
						<Outlet />
					</div>
				</div>

				<ConfirmDialog
					open={deleteTarget != null}
					onConfirm={confirmDelete}
					onCancel={() => setDeleteTarget(null)}
					title="Konversation löschen"
					message="Möchtest du diese Konversation wirklich löschen? Alle Nachrichten gehen verloren."
					confirmLabel="Löschen"
					danger
					loading={deleteConversation.isPending}
				/>
			</>
		);
	}

	// Mobile: Entweder Liste ODER Chat
	return (
		<>
			<div
				className={
					keyboardOpen
						? "fixed inset-x-0 top-0 z-sticky flex bg-background"
						: "-mx-4 -mt-6 -mb-24 flex"
				}
				style={{ height: keyboardOpen ? `${vpHeight}px` : "calc(100dvh - 4rem)" }}
			>
				{activeConversationId ? (
					<div className="flex-1 overflow-hidden">
						<Outlet />
					</div>
				) : (
					<div className="flex-1 overflow-hidden">
						<ConversationList
							conversations={conversations}
							activeConversationId={activeConversationId}
							onSelectConversation={handleSelectConversation}
							onDeleteConversation={handleDeleteConversation}
							isLoading={isLoading}
							hasNextPage={hasNextPage}
							isFetchingNextPage={isFetchingNextPage}
							onFetchNextPage={fetchNextPage}
						/>
					</div>
				)}
			</div>

			<ConfirmDialog
				open={deleteTarget != null}
				onConfirm={confirmDelete}
				onCancel={() => setDeleteTarget(null)}
				title="Konversation löschen"
				message="Möchtest du diese Konversation wirklich löschen? Alle Nachrichten gehen verloren."
				confirmLabel="Löschen"
				danger
				loading={deleteConversation.isPending}
			/>
		</>
	);
}
